import React, { useEffect, useRef, useState } from 'react';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import ChevronLeftIcon from 'mdi-react/ChevronLeftIcon';

/**
 * StickyScrollbar — mirrors a container's horizontal scroll in a fixed bar at the viewport bottom.
 * Also renders a right-edge fade + chevron indicator when more content is available to the right.
 * Works even when ancestors have overflow:auto/hidden (which breaks position:sticky).
 *
 * Automatically detects open MUI overlays (Drawer, Dialog) via MutationObserver and steps
 * behind them — no external prop management needed.
 */
const StickyScrollbar = ({ scrollContainerRef }) => {
  const mirrorRef = useRef(null);
  const syncing = useRef(false);
  const [bar, setBar] = useState(null);
  const [hasMoreRight, setHasMoreRight] = useState(false);
  const [atEnd, setAtEnd] = useState(false);
  const [containerRect, setContainerRect] = useState(null);
  const [overlayOpen, setOverlayOpen] = useState(false);

  // Auto-detect open overlays (MUI Drawer/Dialog + reactstrap Modal)
  useEffect(() => {
    const checkOverlay = () => {
      setOverlayOpen(!!document.querySelector('.MuiBackdrop-root') || !!document.querySelector('.modal-backdrop'));
    };
    const observer = new MutationObserver(checkOverlay);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Track container layout + scroll state
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateLayout = () => {
      const rect = container.getBoundingClientRect();
      const overflows = container.scrollWidth > container.clientWidth;
      const nativeScrollbarOffScreen = rect.bottom > window.innerHeight;
      setContainerRect(rect);
      if (overflows && nativeScrollbarOffScreen) {
        setBar({ left: rect.left, width: rect.width, contentWidth: container.scrollWidth });
      } else {
        setBar(null);
      }
    };

    const updateScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const overflows = container.scrollWidth > container.clientWidth;
      const end = container.scrollLeft + container.clientWidth >= container.scrollWidth - 2;
      setAtEnd(end);
      setHasMoreRight(overflows && !end);
    };

    updateLayout();
    updateScroll();

    const ro = new ResizeObserver(() => { updateLayout(); updateScroll(); });
    ro.observe(container);
    window.addEventListener('resize', updateLayout);
    window.addEventListener('scroll', updateLayout, { passive: true });

    const onContainerScroll = () => {
      updateScroll();
      if (syncing.current) return;
      syncing.current = true;
      if (mirrorRef.current) mirrorRef.current.scrollLeft = container.scrollLeft;
      requestAnimationFrame(() => { syncing.current = false; });
    };
    container.addEventListener('scroll', onContainerScroll);

    return () => {
      container.removeEventListener('scroll', onContainerScroll);
      ro.disconnect();
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('scroll', updateLayout);
    };
  }, [scrollContainerRef]);

  // Sync mirror → container
  // NOTE: `bar` must be in the dep array — the mirror element only mounts when bar !== null,
  // so this effect must re-run after bar first becomes non-null to attach the listener.
  useEffect(() => {
    const mirror = mirrorRef.current;
    if (!mirror) return;
    const onMirrorScroll = () => {
      if (syncing.current) return;
      const container = scrollContainerRef.current;
      if (!container) return;
      syncing.current = true;
      container.scrollLeft = mirror.scrollLeft;
      requestAnimationFrame(() => { syncing.current = false; });
    };
    mirror.addEventListener('scroll', onMirrorScroll);
    return () => mirror.removeEventListener('scroll', onMirrorScroll);
  }, [scrollContainerRef, bar]);

  const behindClass = overlayOpen ? '--behind' : '';

  return (
    <>
      {/* Right-edge chevron — scrolls right when more content available, flips left and scrolls to start when at end */}
      {(hasMoreRight || atEnd) && containerRect && bar && (
        <div
          className={`sticky-scrollbar__chevron sticky-scrollbar__chevron${behindClass}`}
          style={{
            top: Math.max(containerRect.top, 0),
            right: window.innerWidth - containerRect.right - 14,
            height: Math.min(containerRect.height, window.innerHeight - Math.max(containerRect.top, 0)),
          }}
        >
          <div
            className="sticky-scrollbar__chevron-btn"
            onClick={() => {
              const container = scrollContainerRef.current;
              if (!container) return;
              if (atEnd) {
                container.scrollTo({ left: 0, behavior: 'smooth' });
              } else {
                container.scrollBy({ left: 200, behavior: 'smooth' });
              }
            }}
          >
            {atEnd
              ? <ChevronLeftIcon size={20} color="#fff" />
              : <ChevronRightIcon size={20} color="#fff" />
            }
          </div>
        </div>
      )}

      {/* Sticky scrollbar at viewport bottom */}
      {bar && (
        <div
          ref={mirrorRef}
          className={`sticky-scrollbar__track sticky-scrollbar__track${behindClass}`}
          style={{ left: bar.left, width: bar.width }}
        >
          <div style={{ width: bar.contentWidth, height: 1 }} />
        </div>
      )}
    </>
  );
};

export default StickyScrollbar;
